import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  calculateQuote,
  formatGBP,
  NIGERIAN_STATES,
  FIXED_PRICE_KEYS,
} from "../../lib/pricing";
import { buildWhatsAppLink } from "../../lib/whatsapp";
import { Calculator, MessageCircle } from "lucide-react";

const TABS = [
  { id: "standard", label: "Standard Item" },
  { id: "fixed", label: "Fixed-Price Item" },
];

export default function PriceCalculator() {
  const [mode, setMode] = useState("standard");
  const [productValue, setProductValue] = useState("");
  const [weight, setWeight] = useState("");
  const [state, setState] = useState("Lagos");
  const [complex, setComplex] = useState(false);
  const [fixedItem, setFixedItem] = useState(FIXED_PRICE_KEYS[0]);

  const quote = useMemo(() => {
    const pv = parseFloat(productValue) || 0;
    const w = parseFloat(weight) || 0;
    return calculateQuote({
      productValue: pv,
      weightKg: w,
      state,
      isComplexSourcing: complex,
      fixedPriceItem: mode === "fixed" ? fixedItem : null,
    });
  }, [productValue, weight, state, complex, fixedItem, mode]);

  const waMsg =
    `Hi MPS! I'd like to place an order based on this quote: ` +
    `Product £${(parseFloat(productValue) || 0).toFixed(2)}, ` +
    (mode === "fixed" ? `Item: ${fixedItem}, ` : `Weight: ${weight || "?"}kg, `) +
    `State: ${state}, Total: ${formatGBP(quote.total)}.`;

  return (
    <section
      data-testid="price-calculator"
      className="bg-[#F8F5F0] py-20 md:py-28"
    >
      <div className="container-mps">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="overline text-[#C9A96E] mb-3">Quote Calculator</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
              See your <span className="italic text-[#C9A96E]">all-in cost</span> in seconds.
            </h2>
            <p className="mt-6 text-[#6B7280] max-w-xl leading-relaxed">
              Enter the product value, weight, and destination. We compute the service fee,
              freight, and any sourcing add-on — itemised so you know exactly what you&apos;ll pay.
            </p>

            {/* Tabs */}
            <div className="mt-10 flex border border-[#E5E0D8] w-fit">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`calc-tab-${t.id}`}
                  onClick={() => setMode(t.id)}
                  className={`px-6 py-3 text-sm uppercase tracking-[0.16em] transition-colors ${
                    mode === t.id
                      ? "bg-[#0A0A0A] text-[#F8F5F0]"
                      : "bg-[#F8F5F0] text-[#1A1A1A] hover:bg-[#E5E0D8]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {mode === "standard" ? (
                <>
                  <Field label="Product Value (£)">
                    <input
                      data-testid="calc-input-product-value"
                      type="number"
                      min="0"
                      placeholder="e.g. 150"
                      value={productValue}
                      onChange={(e) => setProductValue(e.target.value)}
                      className="input-mps"
                    />
                  </Field>
                  <Field label="Estimated Weight (kg)">
                    <input
                      data-testid="calc-input-weight"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="e.g. 3"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="input-mps"
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Product Value (£)">
                    <input
                      data-testid="calc-input-product-value-fixed"
                      type="number"
                      min="0"
                      placeholder="e.g. 899"
                      value={productValue}
                      onChange={(e) => setProductValue(e.target.value)}
                      className="input-mps"
                    />
                  </Field>
                  <Field label="Item Type">
                    <select
                      data-testid="calc-select-fixed-item"
                      value={fixedItem}
                      onChange={(e) => setFixedItem(e.target.value)}
                      className="input-mps"
                    >
                      {FIXED_PRICE_KEYS.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </Field>
                </>
              )}

              <Field label="Destination State">
                <select
                  data-testid="calc-select-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="input-mps"
                  disabled={mode === "fixed"}
                >
                  {NIGERIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {mode === "fixed" && (
                  <span className="block mt-2 text-xs text-[#6B7280]">
                    Fixed-price items ship at flat rates to Lagos.
                  </span>
                )}
              </Field>

              <label className="flex items-start gap-3 mt-1 cursor-pointer self-end">
                <input
                  data-testid="calc-checkbox-complex"
                  type="checkbox"
                  checked={complex}
                  onChange={(e) => setComplex(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#C9A96E]"
                />
                <span className="text-sm text-[#1A1A1A]">
                  I need MPS to source this — I don&apos;t have a product link
                  <span className="block text-xs text-[#6B7280] mt-1">
                    Adds £30 complex sourcing fee.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Result */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="bg-[#0A0A0A] text-[#F8F5F0] p-10 md:p-12 sticky top-28">
              <div className="flex items-center gap-3 mb-8">
                <Calculator size={22} className="text-[#C9A96E]" strokeWidth={1.5} />
                <div className="overline text-[#C9A96E]">Live Quote</div>
              </div>

              <div className="space-y-5">
                {quote.breakdown.map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between items-start gap-4 text-sm"
                  >
                    <span className="text-[#F8F5F0]/70">{row.label}</span>
                    <span className="font-medium tabular-nums">
                      {formatGBP(row.amount)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="h-px bg-[#C9A96E]/30 my-8" />

              <div className="flex justify-between items-baseline">
                <div className="overline text-[#C9A96E]">Total</div>
                <div
                  data-testid="calc-total-value"
                  className="font-serif text-5xl text-[#C9A96E] tabular-nums"
                >
                  {formatGBP(quote.total)}
                </div>
              </div>

              <a
                href={buildWhatsAppLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-whatsapp-cta"
                className="mt-10 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#25D366] text-[#0A0A0A] text-sm uppercase tracking-[0.18em] hover:bg-[#1EBE5C] transition-colors"
              >
                <MessageCircle size={16} /> Send Quote on WhatsApp
              </a>
              <p className="mt-4 text-xs text-[#F8F5F0]/50 leading-relaxed">
                Estimate only. Final pricing is confirmed once we verify the product
                and dimensions. No payment is taken at this stage.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        .input-mps {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #F8F5F0;
          border: 1px solid #E5E0D8;
          color: #1A1A1A;
          font-size: 1rem;
          outline: none;
        }
        .input-mps:focus {
          border-color: #C9A96E;
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[#1A1A1A] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
